import type { Promotion } from './promotions';

/**
 * What an offer takes money off.
 *
 * Until this existed, a discount had a percentage and nothing to apply it to — the editor
 * never asked which items, because nothing consumed the answer. `discount_percentage` was
 * written, displayed on the card, and read by no order path anywhere: a guest saw "20%
 * OFF" and was charged full price.
 *
 * Deliberately four shapes and no more. "Everything except X" and "items over AED 50" are
 * both askable, and both turn a rule an owner can hold in their head into one they cannot
 * predict at the till.
 */
export type OfferScope =
  | { kind: 'all' }
  | { kind: 'menus'; ids: string[] }
  | { kind: 'categories'; ids: string[] }
  | { kind: 'items'; ids: string[] };

/** The labels the picker offers, in the order an owner thinks about them. */
export const OFFER_SCOPE_KINDS: { value: OfferScope['kind']; label: string; hint: string }[] = [
  { value: 'all',        label: 'Everything',   hint: 'The whole bill' },
  { value: 'menus',      label: 'Whole menus',  hint: 'e.g. the drinks menu' },
  { value: 'categories', label: 'Categories',   hint: 'e.g. Cocktails, Starters' },
  { value: 'items',      label: 'Chosen items', hint: 'Pick the exact dishes' },
];

/** Only these types take money off a bill. A bundle is a fixed price for a set, not a cut. */
export function isDiscountOffer(promo: Pick<Promotion, 'promotion_type'>): boolean {
  return promo.promotion_type === 'happy_hour' || promo.promotion_type === 'discount';
}

/** An order line, as far as an offer is concerned. */
export interface ScopedLine {
  /** The menu item id. Absent for an ad-hoc line a cashier typed in by hand. */
  itemId?: string | null;
  categoryId?: string | null;
  menuId?: string | null;
  price: number;
  quantity: number;
}

/**
 * Does this line fall inside the offer?
 *
 * A line with no menu item id — something a cashier typed in at the till — matches only
 * `all`. It belongs to no category and no menu, so there is nothing for a narrower scope
 * to match it against, and guessing would apply a cocktail discount to a hand-typed
 * "corkage".
 */
export function lineInScope(line: ScopedLine, scope: OfferScope): boolean {
  switch (scope.kind) {
    case 'all':        return true;
    case 'items':      return !!line.itemId && scope.ids.includes(line.itemId);
    case 'categories': return !!line.categoryId && scope.ids.includes(line.categoryId);
    case 'menus':      return !!line.menuId && scope.ids.includes(line.menuId);
  }
}

/** What the offer applies to, in money. */
export function scopedSubtotal(lines: ScopedLine[], scope: OfferScope): number {
  return lines
    .filter(l => lineInScope(l, scope))
    .reduce((sum, l) => sum + l.price * l.quantity, 0);
}

/** Money, to the fils. Floating point makes 0.1 + 0.2 a support ticket. */
export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * What this offer takes off, given the lines it applies to.
 *
 * **Never more than the matching lines are worth.** A AED 50 fixed discount against a
 * AED 20 cocktail takes 20, not 50 — without the cap, a fixed-amount offer on a cheap
 * category hands out money, and on a large enough order it makes the bill negative.
 *
 * Returns 0 when nothing matches, which is the common case: an offer that does not touch
 * this particular basket.
 */
export function discountFor(promo: Promotion, lines: ScopedLine[]): number {
  const scope = promo.offer_scope;
  // No scope means the owner has not said what it applies to. It stays a poster rather
  // than quietly discounting the entire bill the moment this shipped.
  if (!scope || !isDiscountOffer(promo)) return 0;

  const base = scopedSubtotal(lines, scope);
  if (base <= 0) return 0;

  const pct = promo.discount_percentage;
  if (typeof pct === 'number' && pct > 0) {
    return round2(Math.min(base, (base * Math.min(pct, 100)) / 100));
  }

  const amount = promo.discount_amount;
  if (typeof amount === 'number' && amount > 0) return round2(Math.min(base, amount));

  return 0;
}

/** An offer that actually applies to this basket, and what it is worth. */
export interface AppliedOffer {
  promotionId: string;
  promotionName: string;
  amount: number;
}

/**
 * The single best offer for this basket.
 *
 * **One offer per order, not all of them stacked.** Two overlapping happy hours should not
 * silently compound into 40% off, and an owner cannot reason about a bill built from rules
 * that multiply. The guest gets the largest single discount, which is the one they would
 * have picked themselves.
 *
 * Ties break on the id so the same basket always produces the same answer — an order total
 * that changes between the guest's screen and the kitchen's is a dispute at the till.
 */
export function bestOffer(promotions: Promotion[], lines: ScopedLine[]): AppliedOffer | null {
  let best: AppliedOffer | null = null;

  for (const promo of promotions) {
    const amount = discountFor(promo, lines);
    if (amount <= 0) continue;

    if (!best
      || amount > best.amount
      || (amount === best.amount && promo.id < best.promotionId)) {
      best = { promotionId: promo.id, promotionName: promo.name, amount };
    }
  }

  return best;
}

/**
 * Where "Browse Menu" should take a guest reading an offer.
 *
 * An owner who scoped a happy hour to ONE menu has already answered the question the
 * chooser would ask, so the guest goes straight there. Anything else — several menus, a
 * category, individual dishes, or no scope at all — lands on the chooser: the offer does
 * not name a single menu, and guessing one would send some guests to the wrong place.
 *
 * Returns a path suffix to append to `/v/{slug}`, so the caller can still run it through
 * `buildUrl` and keep the guest's ordering mode in the path.
 */
export function offerMenuPath(scope: OfferScope | null | undefined): string {
  return scope?.kind === 'menus' && scope.ids.length === 1
    ? `/menu/${scope.ids[0]}`
    : '/menus';
}
